import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DeployDemo from '../components/DeployDemo';

interface Template {
  id: string;
  name: string;
  slug: string;
  description: string;
  thumbnail_url: string;
  preview_url: string;
  is_free: boolean;
  price_cents: number;
  downloads: number;
  frameworks: string[];
  is_featured: boolean;
}

// Featured templates for homepage teaser
const FEATURED_TEMPLATES: Template[] = [
  {
    id: '1',
    name: 'Modern SaaS Landing',
    slug: 'modern-saas-landing',
    description: 'Stunning dark-mode SaaS landing page',
    thumbnail_url: '',
    preview_url: 'https://landing-demo.wiserwebcloud.com',
    is_free: true,
    price_cents: 0,
    downloads: 1247,
    frameworks: ['html', 'tailwind'],
    is_featured: true,
  },
  {
    id: '2',
    name: 'Developer Portfolio',
    slug: 'developer-portfolio',
    description: 'Clean portfolio for developers',
    thumbnail_url: '',
    preview_url: 'https://portfolio-demo.wiserwebcloud.com',
    is_free: true,
    price_cents: 0,
    downloads: 892,
    frameworks: ['html', 'tailwind'],
    is_featured: true,
  },
  {
    id: '3',
    name: 'React + Vite Starter',
    slug: 'react-vite-starter',
    description: 'Modern React 18 starter with Vite',
    thumbnail_url: '',
    preview_url: 'https://react-starter.wiserwebcloud.com',
    is_free: true,
    price_cents: 0,
    downloads: 2103,
    frameworks: ['react', 'vite', 'tailwind'],
    is_featured: true,
  },
];

const FRAMEWORKS = [
  { id: 'react', label: 'React', color: 'bg-cyan-500/20 text-cyan-400' },
  { id: 'vue', label: 'Vue', color: 'bg-emerald-500/20 text-emerald-400' },
  { id: 'svelte', label: 'Svelte', color: 'bg-orange-500/20 text-orange-400' },
  { id: 'html', label: 'HTML', color: 'bg-red-500/20 text-red-400' },
  { id: 'tailwind', label: 'Tailwind', color: 'bg-sky-500/20 text-sky-400' },
  { id: 'vite', label: 'Vite', color: 'bg-purple-500/20 text-purple-400' },
];

function MiniTemplateCard({ template }: { template: Template }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-orange-500/50 transition-all duration-300"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="aspect-[16/10] bg-gradient-to-br from-orange-600/20 to-red-600/20 relative overflow-hidden">
        {/* Framework badges */}
        <div className="absolute top-2 left-2 flex gap-1 z-10">
          {template.frameworks.slice(0, 2).map((fw) => {
            const framework = FRAMEWORKS.find((f) => f.id === fw);
            return (
              <span
                key={fw}
                className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase ${
                  framework?.color || 'bg-zinc-700/50 text-zinc-300'
                }`}
              >
                {fw}
              </span>
            );
          })}
        </div>

        {template.is_free && (
          <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-emerald-500/90 rounded text-[9px] font-bold text-white z-10">
            FREE
          </div>
        )}

        {/* Preview iframe on hover */}
        <div
          className={`absolute inset-0 transition-opacity duration-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <iframe
            src={template.preview_url}
            className="w-[400%] h-[400%] origin-top-left scale-25 pointer-events-none"
            title={template.name}
          />
        </div>

        {/* Placeholder */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            isHovered ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <span className="material-icons text-4xl text-white/30">web</span>
        </div>

        {/* Hover overlay */}
        <div
          className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-2 transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <a
            href={template.preview_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 bg-white text-black font-medium rounded text-xs hover:bg-zinc-200 transition flex items-center gap-1"
          >
            <span className="material-icons text-sm">visibility</span>
            Preview
          </a>
        </div>
      </div>

      <div className="p-3">
        <h3 className="font-medium text-white text-sm group-hover:text-orange-400 transition line-clamp-1">
          {template.name}
        </h3>
        <p className="text-xs text-zinc-500 mt-1 line-clamp-1">{template.description}</p>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [templates, setTemplates] = useState<Template[]>(FEATURED_TEMPLATES);

  useEffect(() => {
    fetch('https://api.wiserwebcloud.com/api/marketplace/featured')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setTemplates(data.slice(0, 3));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen">
      {/* ========== HERO SECTION ========== */}
      <section className="min-h-screen flex items-center justify-center relative pt-16">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-red-500/10" />
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-orange-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/20 rounded-full blur-3xl" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 py-12">
          <div className="flex flex-col xl:flex-row gap-12 xl:gap-16 items-center">
            {/* Owl Mascot */}
            <div className="flex justify-center xl:justify-end w-full xl:w-1/2 order-2 xl:order-1">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-full blur-3xl scale-75" />
                <svg
                  className="w-48 sm:w-64 md:w-72 xl:w-80 h-48 sm:h-64 md:h-72 xl:h-80 relative z-10"
                  viewBox="0 0 120 120"
                >
                  <defs>
                    <linearGradient id="owlGradHero" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#f97316' }} />
                      <stop offset="100%" style={{ stopColor: '#ea580c' }} />
                    </linearGradient>
                    <linearGradient id="beakGradHero" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#fbbf24' }} />
                      <stop offset="100%" style={{ stopColor: '#f59e0b' }} />
                    </linearGradient>
                  </defs>
                  <ellipse cx="60" cy="65" rx="45" ry="42" fill="url(#owlGradHero)" />
                  <polygon points="22,35 32,18 40,38" fill="url(#owlGradHero)" />
                  <polygon points="98,35 88,18 80,38" fill="url(#owlGradHero)" />
                  <ellipse cx="60" cy="75" rx="25" ry="22" fill="#fdba74" opacity="0.3" />
                  <circle cx="42" cy="55" r="18" fill="#18181b" />
                  <circle cx="42" cy="55" r="12" fill="white" />
                  <circle cx="45" cy="52" r="6" fill="#18181b" />
                  <circle cx="47" cy="50" r="2" fill="white" />
                  <circle cx="78" cy="55" r="18" fill="#18181b" />
                  <circle cx="78" cy="55" r="12" fill="white" />
                  <circle cx="81" cy="52" r="6" fill="#18181b" />
                  <circle cx="83" cy="50" r="2" fill="white" />
                  <polygon points="60,68 52,82 60,90 68,82" fill="url(#beakGradHero)" />
                  <ellipse cx="45" cy="105" rx="8" ry="4" fill="url(#beakGradHero)" />
                  <ellipse cx="75" cy="105" rx="8" ry="4" fill="url(#beakGradHero)" />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="text-center xl:text-left w-full xl:w-1/2 order-1 xl:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-full text-sm text-orange-400 mb-6">
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                Now in Beta
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
                Deploy in{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                  Seconds
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-zinc-400 mb-3">
                AI-powered container hosting for modern developers.
              </p>
              <p className="text-base sm:text-lg text-zinc-500 mb-8 max-w-md mx-auto xl:mx-0">
                Deploy Docker containers, static sites, and databases with a single API call. Built for AI assistants.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center xl:justify-start gap-4">
                <a
                  href="https://dashboard.wiserwebcloud.com/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  Start Free
                  <span className="material-icons">arrow_forward</span>
                </a>
                <Link
                  to="/templates"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 font-medium rounded-xl hover:bg-zinc-800 transition"
                >
                  <span className="material-icons text-lg">widgets</span>
                  Browse Templates
                </Link>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center text-zinc-500">
            <span className="text-xs mb-2">Explore</span>
            <span className="material-icons animate-bounce">expand_more</span>
          </div>
        </div>
      </section>

      {/* ========== TEMPLATE TEASER ========== */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-900/50 to-transparent" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Featured{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                  Templates
                </span>
              </h2>
              <p className="text-zinc-400">Production-ready templates you can deploy in one click</p>
            </div>
            <Link
              to="/templates"
              className="inline-flex items-center gap-2 px-4 py-2 text-orange-400 hover:text-orange-300 font-medium transition"
            >
              View all templates
              <span className="material-icons">arrow_forward</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <MiniTemplateCard key={template.id} template={template} />
            ))}
          </div>
        </div>
      </section>

      {/* ========== DEPLOY DEMO ========== */}
      <DeployDemo />

      {/* ========== FEATURES PREVIEW ========== */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500">
                Ship Fast
              </span>
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Powerful features designed for modern web development
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'bolt', title: 'Instant Deploy', desc: 'Push to deploy in seconds via our API' },
              { icon: 'https', title: 'Free SSL', desc: 'HTTPS enabled automatically for all sites' },
              { icon: 'dns', title: 'Custom Domains', desc: 'Connect your own domain with ease' },
              { icon: 'inventory_2', title: 'Docker Containers', desc: 'Run any app in isolated containers' },
              { icon: 'storage', title: 'Managed Databases', desc: 'PostgreSQL, MariaDB, Redis, MongoDB' },
              { icon: 'smart_toy', title: 'AI-Powered', desc: 'Deploy from your AI coding assistant' },
            ].map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-orange-500/30 transition group"
              >
                <div className="w-12 h-12 mb-4 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl flex items-center justify-center group-hover:from-orange-500/30 group-hover:to-red-500/30 transition">
                  <span className="material-icons text-2xl text-orange-400">{feature.icon}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-800 border border-zinc-700 text-white font-medium rounded-xl hover:bg-zinc-700 transition"
            >
              <span className="material-icons">explore</span>
              Explore All Features
            </Link>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-24 px-6 bg-gradient-to-b from-zinc-900/50 to-base">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-lg text-zinc-400 mb-8">
            Join developers shipping faster with WISERWEB.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://dashboard.wiserwebcloud.com/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
            >
              <span className="material-icons">person_add</span>
              Create Free Account
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-300 font-medium rounded-xl hover:bg-zinc-800 transition"
            >
              <span className="material-icons text-lg">mail</span>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
